import { reactive } from 'vue';

/**
 * ModalService - Abstraction layer for programmatic modals
 *
 * Provides a unified API that works with PrimeVue.
 *
 * Usage:
 *   this.$modal.open({
 *       component: MyComponent,
 *       props: { ... },
 *       parent: this,
 *       canCancel: ['escape', 'outside'],
 *       onCancel: () => { ... }
 *   });
 *
 * @class ModalService
 */
class ModalService {
    constructor() {
        this.app = null;
        this.activeModals = new Map();
        this.modalId = 0;
    }

    /**
     * Initialize the service with the Vue app instance
     * @param {Object} app - Vue app instance
     */
    init(app) {
        this.app = app;
        app.provide('modal', this);
    }

    /**
     * Open a programmatic modal
     *
     * @param {Object} params - Modal configuration
     * @param {Object|string} params.component - Vue component or component name to render
     * @param {Object} [params.props] - Props to pass to the component
     * @param {Object} [params.parent] - Parent component instance (for accessing parent methods/data)
     * @param {Object} [params.events] - Event handlers for the component
     * @param {string} [params.width] - Modal width (e.g., '640px', '50%')
     * @param {boolean} [params.hasModalCard=false] - Whether component uses b-modal-card wrapper
     * @param {Array|boolean} [params.canCancel=['escape', 'x', 'outside']] - How modal can be cancelled
     * @param {Function} [params.onCancel] - Callback when modal is cancelled
     * @param {string} [params.animation='zoom-out'] - Animation type
     * @param {boolean} [params.scroll='keep'] - Scroll behavior
     * @param {boolean} [params.fullScreen=false] - Whether modal should be full screen
     * @param {boolean} [params.trapFocus=true] - Whether to trap focus inside modal
     * @param {string} [params.ariaRole='dialog'] - ARIA role
     * @param {string} [params.ariaModal] - ARIA modal attribute
     * @param {string} [params.customClass] - Additional CSS classes
     * @returns {Object} Modal instance with close() method
     */
    open(params = {}) {
        if (!this.app) {
            console.error('ModalService not initialized. Call init(app) first.');
            return { close: () => {} };
        }

        return this.openPrimeVueModal(params);
    }

    /**
     * Open PrimeVue Dialog modal
     * @private
     */
    openPrimeVueModal(params) {
        const modalId = ++this.modalId;

        // Normalize canCancel to boolean flags
        const canCancel = this.normalizeCanCancel(params.canCancel);

        // Create modal state
        const modalState = reactive({
            id: modalId,
            visible: true,
            component: params.component,
            props: params.props || {},
            parent: params.parent,
            events: params.events || {},
            width: params.width || '640px',
            header: params.header,
            hasModalCard: params.hasModalCard || false,
            canCancel: canCancel,
            onCancel: params.onCancel,
            fullScreen: params.fullScreen || false,
            customClass: params.customClass,
        });

        // Store in active modals
        this.activeModals.set(modalId, modalState);

        // Emit event with just the ID - GlobalModals will retrieve the full state
        window.dispatchEvent(
            new CustomEvent('primevue-modal-open', {
                detail: { id: modalId },
            })
        );

        return {
            close: () => {
                const modal = this.activeModals.get(modalId);
                if (modal) {
                    modal.visible = false;
                }
                window.dispatchEvent(
                    new CustomEvent('primevue-modal-close', {
                        detail: { id: modalId },
                    })
                );
            },
        };
    }

    /**
     * Normalize canCancel parameter to consistent format
     * @private
     * @param {Array|boolean|undefined} canCancel - Various formats of canCancel
     * @returns {Object} Normalized canCancel flags
     */
    normalizeCanCancel(canCancel) {
        // Default: allow all cancel methods
        if (canCancel === undefined || canCancel === true) {
            return {
                escape: true,
                button: true,
                outside: true,
            };
        }

        // If false, disallow all cancel methods
        if (canCancel === false) {
            return {
                escape: false,
                button: false,
                outside: false,
            };
        }

        // If array, check which methods are allowed
        if (Array.isArray(canCancel)) {
            return {
                escape: canCancel.includes('escape'),
                button: canCancel.includes('x') || canCancel.includes('button'),
                outside: canCancel.includes('outside'),
            };
        }

        // Default fallback
        return {
            escape: true,
            button: true,
            outside: true,
        };
    }

    /**
     * Close all active modals
     */
    closeAll() {
        this.activeModals.forEach(modal => {
            modal.visible = false;
        });
        this.activeModals.clear();
        window.dispatchEvent(new CustomEvent('primevue-modal-close-all'));
    }

    /**
     * Get modal state by ID
     * @param {number} id - Modal ID
     * @returns {Object|undefined}
     */
    getModal(id) {
        return this.activeModals.get(id);
    }

    /**
     * Get all active modal states
     * @returns {Array}
     */
    getAllModals() {
        return Array.from(this.activeModals.values());
    }

    /**
     * Check if any modal is currently active
     * @returns {boolean}
     */
    get isActive() {
        return this.activeModals.size > 0;
    }
}

// Create singleton instance
const modalService = new ModalService();

// Vue plugin
export default {
    install(app) {
        modalService.init(app);
        app.config.globalProperties.$modal = modalService;
    },
};

export { modalService as ModalService };
